/* eslint-disable @nx/enforce-module-boundaries */
import { IconButton, ListItem, UnorderedList } from '@mybridge/ui';
import { Button } from '@mybridge/ui/button';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { FormControl, FormErrorMessage } from '@mybridge/ui/form-control';
import { useToast } from '@mybridge/ui/hooks';
import { Input, InputGroup, InputRightElement } from '@mybridge/ui/input';
import { EyeViewIcon, EyeViewFilledIcon } from '@mybridge/icons';
import { HStack, Stack } from '@mybridge/ui/layout';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@mybridge/ui/modal';
import { Text } from '@mybridge/ui/text';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ForgotPasswordReset, setOpenAuthModal } from 'v4/store/actions/auth.actions';

export const ForgotPasswordResetModal = ({ token: propToken, onClose, ...props }) => {
  const { push } = useRouter();
  const dispatch = useDispatch();
  const { openAuthModalData } = useSelector((state) => state.user) ?? {};
  const token = propToken || openAuthModalData?.reset_token || openAuthModalData?.token;
  const { t } = useTranslator();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const toast = useToast({
    position: 'top',
    isClosable: true,
  });

  const onFormSubmit = async ({ password, confirmPassword }) => {
    setLoading(true);
    try {
      const payload = {
        password: password,
        password2: confirmPassword || password,
        token: token,
      };
      const resp = await dispatch(ForgotPasswordReset(payload));
      const { status_code, code, detail, message } = resp?.payload ?? {};

      if (code === 'INVALID_TOKEN' || status_code >= 400 || resp?.error) {
        toast({
          title: detail || message || t('toast.invalidToken') || 'Invalid token! Please try again...',
          status: 'error',
        });
        setLoading(false);
        return;
      } else {
        toast({
          title: t('toast.passwordResetSuccess') || 'Password was reset successfully!',
          status: 'success',
        });
        onClose?.();
        dispatch(setOpenAuthModal('sign-in'));
        push('/');
      }
    } catch (e) {
      toast({
        title: t('toast.passwordResetError') || 'Error while resetting password!',
        description: e?.message,
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal size="lg" isCentered {...props} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius="12px !important">
        <ModalHeader textAlign="center">{t('signingPage.resetPassword') || 'Reset Password'}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <ModalBody pb={6}>
            <Stack spacing={4}>
              <Text textAlign="center">{t('settingAccount.pleaseEnterNewPassword') || 'Please enter new password'}</Text>
              <HStack flex={1}>
                <FormControl isInvalid={errors?.password}>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      {...register('password', {
                        required: true,
                        minLength: 8,
                        pattern:
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                      })}
                      placeholder={t('settingAccount.newPassword') || 'New Password'}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? (t('signingPage.hidePassword') || 'Hide password') : t('signingPage.showPassword') || 'Show password'}
                        icon={showPassword ? <EyeViewFilledIcon /> : <EyeViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {errors?.password && (
                    <FormErrorMessage>
                      <Stack>
                        <Text>{t('activateAccount.validPassword') || 'Please enter a valid password'}</Text>
                        <UnorderedList>
                          <ListItem>{t('signingPage.minCharacters') || 'Min 8 characters'}</ListItem>
                          <ListItem>
                            {t('signingPage.passwordRequirement') || 'At least 1 uppercase, 1 lowercase & 1 special character'}
                          </ListItem>
                          <ListItem>{t('signingPage.numericRequirement') || 'At least 1 numeric'}</ListItem>
                        </UnorderedList>
                      </Stack>
                    </FormErrorMessage>
                  )}
                </FormControl>
              </HStack>
              <HStack flex={1}>
                <FormControl isInvalid={errors?.confirmPassword}>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...register('confirmPassword', {
                        required: true,
                        deps: ['password'],
                        validate: (val) => val === watch('password'),
                      })}
                      placeholder={t('settingAccount.confirmNewPassword') || 'Confirm New Password'}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showConfirmPassword ? (t('signingPage.hidePassword') || 'Hide password') : t('signingPage.showPassword') || 'Show password'}
                        icon={showConfirmPassword ? <EyeViewFilledIcon /> : <EyeViewIcon />}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {errors?.confirmPassword && (
                    <FormErrorMessage>
                      {t('signingPage.confirmPasswordRequired') || 'Please confirm your new password'}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </HStack>
            </Stack>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Stack flex={1}>
              <Button
                borderRadius="30px"
                height="48px"
                py={6}
                fontWeight="bold"
                w="100%"
                type="submit"
                variant="primary"
                isLoading={loading}
              >
                {t('signingPage.resetPassword') || 'Reset Password'}
              </Button>
            </Stack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
